import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Nav, Tab, Spinner, Button } from 'react-bootstrap';
import Wrapper from '../../components/Wrapper';
import {
  getCrop,
  updateCrop,
  putStages,
  upsertPrescription,
  deletePrescription,
  createAllItem,
  updateAllItem,
  deleteAllItem,
  getConstants,
  getStates,
} from '../../services/recommendationsApi';
import CropOverview from './CropOverview';
import CropStages from './CropStages';
import CropPrescriptions from './CropPrescriptions';
import CropAllList from './CropAllList';
import CropFormulaConfigs from './CropFormulaConfigs';

const LANGUAGES = ['english', 'hindi', 'telugu', 'punjabi', 'marathi', 'kannada', 'tamil', 'gujarati', 'odia', 'bengali'];

const RecommendationCropDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const viewMode = location.pathname.endsWith('/view');
  const [crop, setCrop] = useState(null);
  const [constants, setConstants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [states, setStates] = useState([]);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const [cropRes, constRes, statesRes] = await Promise.all([getCrop(slug), getConstants(), getStates()]);
      setCrop(cropRes.crop);
      setConstants(constRes);
      setStates(statesRes);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load crop');
      setCrop(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdateCrop = async (payload) => {
    try {
      const res = await updateCrop(slug, payload);
      setCrop(res.crop);
    } catch (e) {
      throw new Error(e.response?.data?.message || e.message);
    }
  };

  const handlePutStages = async (stages) => {
    try {
      await putStages(slug, stages);
      const res = await getCrop(slug);
      setCrop(res.crop);
      
    } catch (e) {
      throw new Error(e.response?.data?.message || e.message);
    }
  };

  const handleUpsertPrescription = async (nutrientCode, level, body) => {
    await upsertPrescription(slug, nutrientCode, level, body);
    const res = await getCrop(slug);
    setCrop(res.crop);
  };

  const handleDeletePrescription = async (nutrientCode, level, id) => {
    try {
      await deletePrescription(slug, nutrientCode, level, id);
      const res = await getCrop(slug);
      setCrop(res.crop);
    } catch (e) {
      throw new Error(e.response?.data?.message || e.message);
    }
  };

  const handleCreateAllItem = async (body) => {
    await createAllItem(slug, body);
    const res = await getCrop(slug);
    setCrop(res.crop);
  };

  const handleUpdateAllItem = async (id, body) => {
    await updateAllItem(slug, id, body);
    const res = await getCrop(slug);
    setCrop(res.crop);
  };

  const handleDeleteAllItem = async (id) => {
    await deleteAllItem(slug, id);
    const res = await getCrop(slug);
    setCrop(res.crop);
  };

  if (loading) {
    return (
      <Wrapper>
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" />
        </div>
      </Wrapper>
    );
  }

  if (error || !crop) {
    return (
      <Wrapper>
        <div className="py-4">
          <div className="alert alert-danger">{error || 'Crop not found'}</div>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/recommendations')}>
            Back to list
          </button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="fw-bolder">{crop.name_en}{viewMode && <span className="badge bg-secondary ms-2">View only</span>}</h1>
          <div className="d-flex gap-2">
            {viewMode ? (
              <Button variant="primary" onClick={() => navigate(`/recommendations/${encodeURIComponent(slug)}`)}>
                Edit
              </Button>
            ) : null}
            <button className="btn btn-outline-secondary" onClick={() => navigate('/recommendations')}>
              Back to list
            </button>
          </div>
        </div>

        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')}>
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link eventKey="overview">Overview</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="stages">Stages</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="prescriptions">Prescriptions</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="all">All list</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="formulas">Formula Configs</Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content className="pt-3">
            <Tab.Pane eventKey="overview">
              <CropOverview crop={crop} onUpdate={handleUpdateCrop} viewMode={viewMode} />
            </Tab.Pane>
            <Tab.Pane eventKey="stages">
              <CropStages stages={crop.stages || []} onSave={handlePutStages} viewMode={viewMode} />
            </Tab.Pane>
            <Tab.Pane eventKey="prescriptions">
              <CropPrescriptions
                prescriptions={crop.prescriptions || []}
                constants={constants}
                states={states}
                onUpsert={handleUpsertPrescription}
                onDelete={handleDeletePrescription}
                viewMode={viewMode}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="all">
              <CropAllList
                items={crop.all || []}
                states={states}
                onAdd={handleCreateAllItem}
                onUpdate={handleUpdateAllItem}
                onDelete={handleDeleteAllItem}
                viewMode={viewMode}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="formulas">
              <CropFormulaConfigs slug={slug} viewMode={viewMode} />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </Wrapper>
  );
};

export default RecommendationCropDetail;
export { LANGUAGES };
